import { useMemo, useCallback, useId } from 'react'
import { Button } from '@/components/Button'

interface CalendarProps {
	value?: string // ISO YYYY-MM-DD
	onValueChange?: (value: string) => void
	month?: number // 0-11
	year?: number
	onMonthChange?: (month: number, year: number) => void
	minDate?: string
	maxDate?: string
	className?: string
}

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
]

function pad (n: number) {
	return n.toString().padStart(2, '0')
}

const dayLabelFormat = new Intl.DateTimeFormat('en-US', {
	weekday: 'long',
	month: 'long',
	day: 'numeric',
	year: 'numeric'
})

function CalendarCell ({
	date,
	isSelected,
	isToday,
	isDisabled,
	onClick
}: {
	date: Date
	isSelected: boolean
	isToday: boolean
	isDisabled: boolean
	onClick: () => void
}) {
	const appearance = isSelected
		? 'spaces:bg-brand-base spaces:text-brand-on-base'
		: isToday
		? 'spaces:bg-canvas-4 spaces:text-content-1'
		: 'spaces:text-content-2 spaces:hover:bg-canvas-4'

	const state = isDisabled
		? 'spaces-disabled spaces:opacity-disabled spaces:cursor-not-allowed'
		: 'spaces:cursor-pointer'

	return (
		<button
			type='button'
			disabled={isDisabled}
			onClick={onClick}
			aria-label={dayLabelFormat.format(date)}
			aria-pressed={isSelected}
			aria-current={isToday ? 'date' : undefined}
			className={[
				'spaces-calendar-day spaces:flex-1 spaces:min-w-0 spaces:h-40 spaces:flex spaces:items-center spaces:justify-center spaces:text-16/24 spaces:font-default spaces:rounded-8 spaces:transition-colors',
				isSelected ? 'spaces-selected' : '',
				isToday ? 'spaces-today' : '',
				appearance,
				state
			]
				.filter(Boolean)
				.join(' ')}
		>
			{date.getDate()}
		</button>
	)
}

export function Calendar ({
	value,
	onValueChange,
	month: controlledMonth,
	year: controlledYear,
	onMonthChange,
	minDate,
	maxDate,
	className = ''
}: CalendarProps) {
	const headingId = useId()
	const today = new Date()
	const selectedDate = value ? new Date(value + 'T00:00:00') : null

	const displayMonth =
		controlledMonth ?? selectedDate?.getMonth() ?? today.getMonth()
	const displayYear =
		controlledYear ?? selectedDate?.getFullYear() ?? today.getFullYear()

	const navigate = useCallback(
		(dir: -1 | 1) => {
			let m = displayMonth + dir
			let y = displayYear
			if (m < 0) {
				m = 11
				y--
			}
			if (m > 11) {
				m = 0
				y++
			}
			onMonthChange?.(m, y)
		},
		[displayMonth, displayYear, onMonthChange]
	)

	const weeks = useMemo(() => {
		const first = new Date(displayYear, displayMonth, 1)
		const startDay = first.getDay()
		const start = new Date(first)
		start.setDate(start.getDate() - startDay)

		const rows: Date[][] = []
		const cursor = new Date(start)
		for (let w = 0; w < 6; w++) {
			const week: Date[] = []
			for (let d = 0; d < 7; d++) {
				week.push(new Date(cursor))
				cursor.setDate(cursor.getDate() + 1)
			}
			rows.push(week)
			if (cursor.getMonth() !== displayMonth && w >= 4) break
		}
		return rows
	}, [displayMonth, displayYear])

	const isDisabled = useCallback(
		(d: Date) => {
			if (minDate && d < new Date(minDate + 'T00:00:00')) return true
			if (maxDate && d > new Date(maxDate + 'T00:00:00')) return true
			return false
		},
		[minDate, maxDate]
	)

	// Month index as a single comparable number (year * 12 + month)
	const displayIndex = displayYear * 12 + displayMonth
	const monthIndex = (iso: string) => {
		const [y, m] = iso.split('-').map(Number)
		return y * 12 + (m - 1)
	}
	const prevDisabled = !!minDate && displayIndex <= monthIndex(minDate)
	const nextDisabled = !!maxDate && displayIndex >= monthIndex(maxDate)

	return (
		<div className={`spaces-calendar spaces:flex spaces:flex-col spaces:gap-4 ${className}`}>
			<div className='spaces:flex spaces:gap-8 spaces:items-center spaces:justify-center'>
				<Button
					iconOnly='CaretLeft'
					appearance='ghost'
					color='content4'
					size='md'
					aria-label='Previous month'
					disabled={prevDisabled}
					onClick={() => navigate(-1)}
				/>
				<span
					id={headingId}
					aria-live='polite'
					className='spaces:flex-1 spaces:min-w-0 spaces:text-16/24 spaces:font-strong spaces:text-content-1 spaces:text-center'
				>
					{MONTHS[displayMonth]} {displayYear}
				</span>
				<Button
					iconOnly='CaretRight'
					appearance='ghost'
					color='content4'
					size='md'
					aria-label='Next month'
					disabled={nextDisabled}
					onClick={() => navigate(1)}
				/>
			</div>

			{/* Each day's label already names its weekday. */}
			<div aria-hidden='true' className='spaces:flex spaces:items-center'>
				{DAYS.map((d, i) => (
					<div
						key={i}
						className='spaces:flex-1 spaces:min-w-0 spaces:flex spaces:items-center spaces:justify-center spaces:text-12/16 spaces:font-strong spaces:text-content-3'
					>
						{d}
					</div>
				))}
			</div>

			<div role='group' aria-labelledby={headingId} className='spaces:flex spaces:flex-col'>
				{weeks.map((week, wi) => (
					<div key={wi} className='spaces:flex'>
						{week.map((date, di) => {
							const isCurrentMonth = date.getMonth() === displayMonth

							if (!isCurrentMonth) {
								return <div key={di} className='spaces:flex-1 spaces:min-w-0 spaces:h-40 spaces:rounded-8' />
							}

							const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
								date.getDate()
							)}`
							return (
								<CalendarCell
									key={di}
									date={date}
									isSelected={value === iso}
									isToday={date.toDateString() === today.toDateString()}
									isDisabled={isDisabled(date)}
									onClick={() => onValueChange?.(iso)}
								/>
							)
						})}
					</div>
				))}
			</div>
		</div>
	)
}
